import { combineReducers, configureStore } from "@reduxjs/toolkit"
import { persistReducer, persistStore } from "redux-persist"
import createWebStorage from "redux-persist/lib/storage/createWebStorage"
import { tokenSyncMiddleware } from "./middleware/tokenSyncMiddleware"
import { authSlice } from "./slices/authSlice"
import clientReducer from "./slices/clientSlice"

// Create a no-op storage for server-side rendering
const createNoopStorage = () => ({
  getItem(_key: string) {
    return Promise.resolve(null)
  },
  setItem(_key: string, value: unknown) {
    return Promise.resolve(value)
  },
  removeItem(_key: string) {
    return Promise.resolve()
  }
})

// Use localStorage in browser, no-op storage on server
const storage =
  typeof window !== "undefined"
    ? createWebStorage("local")
    : createNoopStorage()

// Persist config for auth
const authPersistConfig = {
  key: "auth",
  storage
}

// Persist config for clients - only persist current client data
const clientsPersistConfig = {
  key: "clients",
  storage,
  whitelist: ["currentClient", "currentClientCategories"] // Only persist current client data
}

const rootReducer = combineReducers({
  auth: persistReducer(authPersistConfig, authSlice.reducer),
  clients: persistReducer(clientsPersistConfig, clientReducer)
})

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ["persist/PERSIST", "persist/REHYDRATE"]
      }
    }).concat(tokenSyncMiddleware),
  devTools: process.env.NODE_ENV !== "production"
})

export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch

export const persistor = persistStore(store)
